/**
 * Electron-compatible localStorage Wrapper
 * Behebt alle Electron-spezifischen Probleme mit localStorage
 * - Quota-Überprüfung
 * - Fehlerbehandlung
 * - Build-Kompatibilität
 * - Persistenz bei Updates
 */

class ElectronLocalStorageWrapper {
  constructor() {
    this.isElectron = this.detectElectron();
    this.quotaLimit = 5 * 1024 * 1024; // 5MB (konservativ für Electron)
    // Store reference to ORIGINAL localStorage before we replace it
    this._originalLocalStorage = window.localStorage;
    this.storageAvailable = this.checkStorageAvailability();
    this.fallbackStorage = new Map(); // In-Memory Fallback
    this._lengthWarningLogged = false; // Prevent infinite warning spam
    this._getAllKeysCache = null; // Cache for getAllKeys to prevent repeated calls
    this._cacheTimeout = null;
    this._errorLogThrottle = {}; // Throttle error logging by key
  }

  /**
   * Erkennt ob wir in Electron laufen
   */
  detectElectron() {
    try {
      return !!(window.electronAPI || window.process?.type === 'renderer' || navigator.userAgent.includes('Electron'));
    } catch (error) {
      return false;
    }
  }

  /**
   * Prüft ob localStorage verfügbar ist
   */
  checkStorageAvailability() {
    try {
      const testKey = '__electron_storage_test__';
      this._originalLocalStorage.setItem(testKey, 'test');
      this._originalLocalStorage.removeItem(testKey);
      return true;
    } catch (error) {
      console.warn('⚠️ [ELECTRON STORAGE] localStorage not available, using fallback:', error);
      return false;
    }
  }

  /**
   * Berechnet aktuellen Storage-Verbrauch
   */
      getStorageUsage() {
    if (!this.storageAvailable) return { used: 0, available: 0, percentage: 0 };

    try {
      let total = 0;
      for (let i = 0; i < this._originalLocalStorage.length; i++) {
        const key = this._originalLocalStorage.key(i);
        const value = this._originalLocalStorage.getItem(key);
        if (key && value) {
          total += key.length + value.length;
        }
      }
      return {
        used: total,
        available: this.quotaLimit,
        percentage: (total / this.quotaLimit) * 100
      };
    } catch (error) {
      console.error('❌ [ELECTRON STORAGE] Error calculating storage usage:', error);
      return { used: 0, available: 0, percentage: 0 };
    }
  }

  /**
   * Prüft ob genug Speicherplatz verfügbar ist
   */
  hasEnoughSpace(requiredBytes) {
    const usage = this.getStorageUsage();
    return (usage.used + requiredBytes) < this.quotaLimit * 0.9; // 90% Threshold
  }

  /**
   * Clear cache when storage changes
   */
  _clearCache() {
    this._getAllKeysCache = null;
    if (this._cacheTimeout) {
      clearTimeout(this._cacheTimeout);
      this._cacheTimeout = null;
    }
  }

  /**
   * Sichere setItem mit vollständiger Fehlerbehandlung
   */
  setItem(key, value) {
    // ✅ FIX: Serialisierung außerhalb try-catch, damit sie im catch-Block verfügbar ist
    let serialized;
    try {
      // Validierung
      if (!key || typeof key !== 'string') {
        throw new Error('Invalid key: must be a non-empty string');
      }

      // Serialisierung
      serialized = typeof value === 'string' ? value : JSON.stringify(value);
      const size = key.length + serialized.length;

      // Quota-Prüfung
      if (!this.hasEnoughSpace(size)) {
        console.warn(`⚠️ [ELECTRON STORAGE] Quota limit approaching for key: ${key}`);
        // Versuche Cleanup
        this.cleanupOldData();
        
        // Prüfe nochmal
        if (!this.hasEnoughSpace(size)) {
          throw new Error('Storage quota exceeded');
        }
      }

      // Speichere in localStorage - use ORIGINAL localStorage to avoid circular calls
      if (this.storageAvailable) {
        this._originalLocalStorage.setItem(key, serialized);
      } else {
        // Fallback zu In-Memory
        this.fallbackStorage.set(key, serialized);
        console.warn(`⚠️ [ELECTRON STORAGE] Using fallback storage for key: ${key}`);
      }

      // Clear cache when storage changes
      this._clearCache();

      return { success: true };
    } catch (error) {
      // ✅ FIX: Serialisiere im catch-Block falls noch nicht geschehen
      if (!serialized) {
        serialized = typeof value === 'string' ? value : JSON.stringify(value);
      }

      // QuotaExceededError oder andere Fehler
      if (error.name === 'QuotaExceededError' || error.message.includes('quota')) {
        console.error(`❌ [ELECTRON STORAGE] Quota exceeded for key: ${key}`, error);
        
        // Versuche Cleanup
        this.cleanupOldData();
        
        // Retry mit kleineren Daten
        try {
          if (this.storageAvailable) {
            this._originalLocalStorage.setItem(key, serialized.substring(0, 1000)); // Truncate
            return { success: true, truncated: true };
          }
        } catch (retryError) {
          // Fallback zu In-Memory
          this.fallbackStorage.set(key, serialized);
          return { success: true, fallback: true };
        }
      }

      // Throttle error logging to prevent spam
      const now = Date.now();
      if (!this._errorLogThrottle[`set_${key}`] || now - this._errorLogThrottle[`set_${key}`] > 5000) {
        console.error(`❌ [ELECTRON STORAGE] Error setting item ${key}:`, error);
        this._errorLogThrottle[`set_${key}`] = now;
      }
      
      // Fallback zu In-Memory
      this.fallbackStorage.set(key, serialized);
      return { success: true, fallback: true, error: error.message };
    }
  }

  /**
   * Sichere getItem mit vollständiger Fehlerbehandlung
   */
  getItem(key) {
    try {
      if (!key || typeof key !== 'string') {
        return null;
      }

      // Versuche localStorage zuerst - use ORIGINAL localStorage to avoid circular calls
      if (this.storageAvailable) {
        const value = this._originalLocalStorage.getItem(key);
        if (value !== null) {
          // Check if value is the corrupted "[object Object]" string
          if (value === '[object Object]' || value === '"[object Object]"') {
            console.warn(`⚠️ [ELECTRON STORAGE] Corrupted data found for key "${key}": "[object Object]" - returning null`);
            // Clean up corrupted data
            this._originalLocalStorage.removeItem(key);
            return null;
          }
          
          // Versuche zu parsen
          try {
            const parsed = JSON.parse(value);
            return parsed;
          } catch (parseError) {
            // Wenn Parsing fehlschlägt, gib String zurück (aber nicht "[object Object]")
            if (value !== '[object Object]' && value !== '"[object Object]"') {
              return value;
            }
            // Clean up corrupted data
            this._originalLocalStorage.removeItem(key);
            return null;
          }
        }
      }

      // Fallback zu In-Memory
      const fallbackValue = this.fallbackStorage.get(key);
      if (fallbackValue !== undefined) {
        // Check if value is corrupted
        if (fallbackValue === '[object Object]' || fallbackValue === '"[object Object]"') {
          this.fallbackStorage.delete(key);
          return null;
        }
        try {
          return JSON.parse(fallbackValue);
        } catch (parseError) {
          if (fallbackValue !== '[object Object]' && fallbackValue !== '"[object Object]"') {
            return fallbackValue;
          }
          this.fallbackStorage.delete(key);
          return null;
        }
      }

      return null;
    } catch (error) {
      // Throttle error logging to prevent spam
      const now = Date.now();
      if (!this._errorLogThrottle[`get_${key}`] || now - this._errorLogThrottle[`get_${key}`] > 5000) {
        console.error(`❌ [ELECTRON STORAGE] Error getting item ${key}:`, error);
        this._errorLogThrottle[`get_${key}`] = now;
      }
      return null;
    }
  }

  /**
   * Sichere removeItem
   */
  removeItem(key) {
    try {
      if (this.storageAvailable) {
        this._originalLocalStorage.removeItem(key);
      }
      this.fallbackStorage.delete(key);
      
      // Clear cache when storage changes
      this._clearCache();
      return { success: true };
    } catch (error) {
      console.error(`❌ [ELECTRON STORAGE] Error removing item ${key}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Bereinigt alte Daten wenn Quota erreicht wird
   */
  cleanupOldData() {
    try {
      const usage = this.getStorageUsage();
      if (usage.percentage < 80) return; // Nur wenn über 80%

      console.log('🧹 [ELECTRON STORAGE] Starting cleanup...');

      // Lösche alte Cache-Daten
      const keysToDelete = [];
      for (let i = 0; i < this._originalLocalStorage.length; i++) {
        const key = this._originalLocalStorage.key(i);
        if (key && (key.startsWith('cache_') || key.startsWith('temp_'))) {
          keysToDelete.push(key);
        }
      }

      keysToDelete.forEach(key => {
        try {
          this._originalLocalStorage.removeItem(key);
        } catch (error) {
          // Ignore
        }
      });

      console.log(`✅ [ELECTRON STORAGE] Cleaned up ${keysToDelete.length} items`);
    } catch (error) {
      console.error('❌ [ELECTRON STORAGE] Error during cleanup:', error);
    }
  }

  /**
   * Prüft ob Key existiert
   */
  hasItem(key) {
    try {
      if (this.storageAvailable) {
        return this._originalLocalStorage.getItem(key) !== null;
      }
      return this.fallbackStorage.has(key);
    } catch (error) {
      return false;
    }
  }

  /**
   * Gibt alle Keys zurück
   */
  getAllKeys() {
    // Return cached result if available (prevents infinite loops)
    if (this._getAllKeysCache !== null) {
      return this._getAllKeysCache;
    }
    
    try {
      const keys = [];
      if (this.storageAvailable) {
        try {
          // Safely get localStorage length - use ORIGINAL localStorage
          const length = this._originalLocalStorage.length;
          for (let i = 0; i < length; i++) {
            try {
              const key = this._originalLocalStorage.key(i);
              if (key) keys.push(key);
            } catch (keyError) {
              // Skip this key if it fails
              continue;
            }
          }
        } catch (lengthError) {
          // If localStorage.length fails, just use fallback
          // Only log warning once to prevent spam
          if (!this._lengthWarningLogged) {
            console.warn('⚠️ [ELECTRON STORAGE] localStorage.length failed, using fallback only');
            this._lengthWarningLogged = true;
          }
        }
      }
      // Füge Fallback-Keys hinzu
      try {
        this.fallbackStorage.forEach((value, key) => {
          if (!keys.includes(key)) {
            keys.push(key);
          }
        });
      } catch (forEachError) {
        // Fallback iteration failed, return what we have
      }
      
      // Cache the result for 100ms to prevent rapid repeated calls
      this._getAllKeysCache = keys;
      if (this._cacheTimeout) {
        clearTimeout(this._cacheTimeout);
      }
      this._cacheTimeout = setTimeout(() => {
        this._getAllKeysCache = null;
      }, 100);
      
      return keys;
    } catch (error) {
      // Final fallback - return empty array to prevent infinite loops
      if (!this._lengthWarningLogged) {
        console.warn('⚠️ [ELECTRON STORAGE] getAllKeys failed, returning empty array:', error.message);
        this._lengthWarningLogged = true;
      }
      return [];
    }
  }

  /**
   * Exportiert alle Daten (für Backup)
   */
  exportAllData() {
    try {
      const data = {};
      const keys = this.getAllKeys();
      keys.forEach(key => {
        data[key] = this.getItem(key);
      });
      return { success: true, data };
    } catch (error) {
      console.error('❌ [ELECTRON STORAGE] Error exporting data:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Importiert Daten (für Restore)
   */
  importData(data) {
    try {
      let imported = 0;
      let failed = 0;

      Object.entries(data).forEach(([key, value]) => {
        const result = this.setItem(key, value);
        if (result.success) {
          imported++;
        } else {
          failed++;
        }
      });

      return { success: true, imported, failed };
    } catch (error) {
      console.error('❌ [ELECTRON STORAGE] Error importing data:', error);
      return { success: false, error: error.message };
    }
  }
}

// Singleton-Instanz
const electronStorageWrapper = new ElectronLocalStorageWrapper();

/**
 * Electron-Storage-Diagnose (ADR-0002: kein window.localStorage-Monkey-Patch).
 */
export const initializeElectronStorage = () => {
  try {
    if (electronStorageWrapper.isElectron) {
      const usage = electronStorageWrapper.getStorageUsage();
      if (!electronStorageWrapper.storageAvailable) {
        console.warn('⚠️ [ELECTRON STORAGE] Native localStorage unavailable — IDB is primary');
      } else if (usage.percentage > 85) {
        console.warn(`⚠️ [ELECTRON STORAGE] Usage high (${usage.percentage.toFixed(1)}%)`);
      } else {
        console.log('✅ [ELECTRON STORAGE] Diagnostics OK (no localStorage patch — ADR-0002)');
      }
    }
    return {
      success: true,
      isElectron: electronStorageWrapper.isElectron,
      storageAvailable: electronStorageWrapper.storageAvailable,
      usage: electronStorageWrapper.getStorageUsage(),
      patched: false
    };
  } catch (error) {
    console.error('❌ [ELECTRON STORAGE] Error initializing:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Direkte Nutzung des Wrappers (wenn localStorage nicht ersetzt werden soll)
 */
export const getElectronStorage = () => electronStorageWrapper;

export default electronStorageWrapper;

